import { OrderSearchConditionEntity } from 'src/entities/OrderSearchCondition.entity';

import { FilterColumnDef, FilterColumnType, FilterOperator, FilterRow, FilterValue } from './types';

export type LegacyImportIssue = {
  /** 旧条件の並び順（表示用）。プリセット全体を落とす tooManyRows だけは -1 */
  index: number;
  key: string;
  operator: string;
  reason: 'unknownColumn' | 'unknownCustomField' | 'unsupportedOperator'
        | 'unparsableValue' | 'unresolvableId' | 'mastersUnavailable' | 'tooManyRows';
  /**
   * tooManyRows のときの展開後行数。表示文「条件が多く、この画面の形式に収まりません（N 行）」の
   * N はここから取る。rows は空で返すので表示側では数えられない。
   */
  rowCount?: number;
};

export type LegacyImportNotice =
  | 'notContains' | 'itemNameWithSpace' | 'itemNameEquals' | 'expanded'
  | 'revivedColumn' | 'caseInsensitive' | 'timeAcrossDays' | 'idSelectByName';

export type LegacyImportResult = {
  rows: FilterRow[];
  issues: LegacyImportIssue[];
  notices: LegacyImportNotice[];
  blocked: boolean;
  needsConfirmation: boolean;
};

export type LegacyMastersState = {
  isLicenseReady: boolean;
  isMastersReady: boolean;
  isMastersError: boolean;
};

/** 旧画面（/plans）の検索演算子。localStorage 由来の値を検証するのでここから export する。 */
export const LEGACY_OPERATORS = [
  '含む', '含まない', '完全一致', '以上', '以下', '等しい', '可', '不可',
  'よりも未来', 'よりも過去', '同日時',
] as const;

/**
 * 旧キー → 新しい列キー。旧画面の `SearchOrderProperties`
 * （`V2Plans/presenters/OrdersPresenter.tsx`）のうち新画面にも列があるものだけを載せる。
 */
const LEGACY_KEY_TO_COLUMN: Record<string, string> = {
  code: 'code',
  memo: 'memo',
  shipperName: 'shipperName',
  loadingName: 'loadingName',
  loadingAddress: 'loadingAddress',
  unloadingName: 'unloadingName',
  unloadingAddress: 'unloadingAddress',
  itemHandlingOfCargoStyle: 'handlingOfCargoStyle',
  itemName: 'itemNames',
  itemPackingStyle: 'itemPackingStyles',
  itemCount: 'cases',
  itemTotalWeightKg: 'weight',
  itemTotalVolumeM3: 'volume',
  loadingStartAt: 'loadingStartAt',
  loadingEndAt: 'loadingEndAt',
  unloadingStartAt: 'unloadingStartAt',
  unloadingEndAt: 'unloadingEndAt',
  itemCanBeMixed: 'canBeMixed',
  designatedTruckKlasses: 'truckKlasses',
  designatedTruckCarModels: 'carModels',
  designatedTruckLoadingPlatformHeights: 'platformHeights',
  designatedTruckLoadingPlatformWidths: 'platformWidths',
  designatedTruckLoadingPlatformLengths: 'platformLengths',
  designatedTruckFloorSpecifications: 'floorSpecifications',
  designatedTruckFeatures: 'truckFeatures',
  allowedTrucks: 'allowedTruckIds',
  deniedDrivers: 'deniedDriverIds',
};

/**
 * 旧画面にはあるが新画面には列が無いキー。
 * これを列挙しておかないと任意項目と区別できず、`unknownCustomField`（任意項目が無い）と
 * 誤った理由を出してしまう。
 */
const LEGACY_ONLY_KEYS = new Set([
  'shipperContactPerson', 'shipperPhoneNumber', 'shipperEmailAddress', 'itemKlass',
  'chargeBasicFeeYen', 'chargeHighwayFeeYen', 'chargeLoadingFeeYen', 'chargeAncillaryFeeYen',
  'chargeWaitingTimeFeeYen', 'chargeUnloadingFeeYen', 'chargeExpensesFeeYen', 'chargeAncillaryContent',
]);

/** 旧演算子 → 新演算子。列の型ごとに引ける組み合わせだけを持つ。 */
const OPERATOR_MAP: Partial<Record<FilterColumnType, Partial<Record<string, FilterOperator>>>> = {
  text: { 含む: 'contains', 含まない: 'notContains', 完全一致: 'equals' },
  number: { 以上: 'gte', 以下: 'lte', 等しい: 'eq' },
  time: { よりも未来: 'timeGte', よりも過去: 'timeLte', 同日時: 'timeEq' },
  // 完全一致 も単一値の anyOf に写す。anyOf は Set での一致判定なので、
  // 「その値が指定されているか」という作成者の意図には最も近い
  enumSelect: { 含む: 'anyOf', 含まない: 'noneOf', 完全一致: 'anyOf' },
  idSelect: { 含む: 'anyOf', 含まない: 'noneOf', 完全一致: 'anyOf' },
  // 演算子が可否そのものなので新画面の演算子は 1 種類。可否は値側で表す
  boolean: { 可: 'is', 不可: 'is' },
};

/**
 * 旧画面では評価が壊れていた列。値を `String()` できず `''` と比較していたため、
 * 「含む」は 0 件・「含まない」は全件一致になっていた。取り込むと結果が変わる。
 */
const REVIVED_COLUMN_KEYS = new Set([
  'truckKlasses', 'carModels', 'platformHeights', 'platformWidths', 'platformLengths',
  'floorSpecifications', 'truckFeatures',
  // 旧は entity['item_packing_style'] を見るが、この項目は items[] にしか無い
  'itemPackingStyles',
  // 旧は false が "false" になり !!"false" が true。「可」で全件一致していた
  'canBeMixed',
]);

const TIME_PATTERN = /^([01]?\d|2[0-3]):[0-5]\d$/;

/** DNF 展開後に許す行数の上限。本番データの実測最大は 36 行。 */
const MAX_EXPANDED_ROWS = 100;

/**
 * 旧の時刻値を `HH:mm` へ正規化する。`isActiveFilterRow` の時刻判定はゼロ埋め必須なので、
 * `9:00` のような値はそのまま入れると無効行になる。
 *
 * Why not evaluate.ts の toHHmm（Asia/Tokyo 固定）を使う: ここで扱うのは案件の日時ではなく
 * 旧画面が保存した「条件の値」で、旧画面はローカルタイムゾーンの時分として書き出し、
 * 読み戻すときも同じ救済（`V3Plans/presenters/OrdersPresenter.tsx` の日時→時刻変換）で
 * 表示していた。取り込みは作成者が旧画面で見ていた時刻に一致させる。
 */
function normalizeTimeValue(value: string): string | null {
  if (value.includes('T')) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return `${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}`;
  }
  if (!TIME_PATTERN.test(value)) return null;
  const [hour, minute] = value.split(':');
  return `${hour.padStart(2, '0')}:${minute}`;
}

type Converted = {
  index: number;
  legacyKey: string;
  legacyOperator: string;
  column: FilterColumnDef;
  operator: FilterOperator;
  value: FilterValue;
  rawValue: string;
};

/**
 * Why not `{ ok: boolean }` の判別子: tsconfig が strict:false なので boolean リテラルの
 * 判別子では `if (!outcome.ok)` で narrowing が効かない（TS2339 になる）。
 * このファイルの他の分岐と同じく `'reason' in x` で判別する。
 */
type ConvertOutcome =
  | { converted: Converted }
  | { reason: Exclude<LegacyImportIssue['reason'], 'tooManyRows'> };

function resolveColumn(
  key: string,
  columnsByKey: Map<string, FilterColumnDef>,
): { column: FilterColumnDef } | { reason: 'unknownColumn' | 'unknownCustomField' } {
  const mapped = LEGACY_KEY_TO_COLUMN[key];
  if (mapped) {
    const column = columnsByKey.get(mapped);
    return column ? { column } : { reason: 'unknownColumn' };
  }
  if (LEGACY_ONLY_KEYS.has(key)) return { reason: 'unknownColumn' };

  // 旧画面は任意項目を項目名そのままのキーで保存する。新画面は `custom:` を前置する
  const custom = columnsByKey.get(`custom:${key}`);
  return custom ? { column: custom } : { reason: 'unknownCustomField' };
}

type ValueOutcome =
  | { value: FilterValue }
  | { reason: 'unparsableValue' | 'unresolvableId' | 'mastersUnavailable' };

/**
 * 旧は案件に設定された名前を空白で連結した 1 本の文字列と比較する。新は ID の集合との
 * 交差を見るので、名前から候補 ID を集めて写す。
 * 「含む」を部分一致で集めるのは、`'号'` のような入力が完全一致では解決できず、
 * 旧では複数車両に一致していたため（完全一致だけに寄せると意味が変わる）。
 */
function resolveIdValues(
  options: { value: number; label: string }[],
  legacyOperator: string,
  rawValue: string,
  masters: LegacyMastersState,
): ValueOutcome {
  // 取得失敗のときは候補が空になる。unresolvableId にすると「候補が無い」のか
  // 「本当に一致しない」のか区別できないまま条件を落としてしまう
  if (masters.isMastersError) return { reason: 'mastersUnavailable' };
  // label.includes('') は全候補に一致する。空値を通すと条件が事実上消える
  if (rawValue.trim() === '') return { reason: 'unparsableValue' };

  const matched = legacyOperator === '完全一致'
    ? options.filter((o) => o.label === rawValue)
    : options.filter((o) => o.label.includes(rawValue));
  // 先頭 1 件だけを黙って選ぶことはしない（意図しない候補で絞り込まれる）
  if (matched.length === 0) return { reason: 'unresolvableId' };

  return { value: { kind: 'id', values: matched.map((o) => o.value) } };
}

function convertValue(
  column: FilterColumnDef,
  legacyOperator: string,
  rawValue: string,
  masters: LegacyMastersState,
): ValueOutcome {
  switch (column.type) {
    case 'text':
      return rawValue.trim() === '' ? { reason: 'unparsableValue' } : { value: { kind: 'text', value: rawValue } };
    case 'number': {
      // Number('') は 0 になるため、空値を先に弾かないと「0 以上」で全件が通る
      if (rawValue.trim() === '') return { reason: 'unparsableValue' };
      const parsed = Number(rawValue);
      return Number.isFinite(parsed) ? { value: { kind: 'number', value: parsed } } : { reason: 'unparsableValue' };
    }
    case 'time': {
      const normalized = normalizeTimeValue(rawValue);
      return normalized ? { value: { kind: 'time', value: normalized } } : { reason: 'unparsableValue' };
    }
    case 'enumSelect':
      return column.options.some((o) => o.value === rawValue)
        ? { value: { kind: 'enum', values: [rawValue] } }
        : { reason: 'unparsableValue' };
    case 'idSelect':
      return resolveIdValues(column.options, legacyOperator, rawValue, masters);
    // 可 / 不可 は演算子だけで意味が決まる。旧画面も value を見ていないので値は検証しない
    case 'boolean':
      return { value: { kind: 'boolean', value: legacyOperator === '可' } };
    default:
      return { reason: 'unparsableValue' };
  }
}

function convertCondition(
  condition: OrderSearchConditionEntity,
  index: number,
  columnsByKey: Map<string, FilterColumnDef>,
  masters: LegacyMastersState,
): ConvertOutcome {
  const legacyKey = condition.key ?? '';
  const legacyOperator = condition.operator ?? '';
  const rawValue = condition.value ?? '';

  const resolved = resolveColumn(legacyKey, columnsByKey);
  if ('reason' in resolved) return { reason: resolved.reason };
  const { column } = resolved;

  const operator = OPERATOR_MAP[column.type]?.[legacyOperator];
  if (!operator) return { reason: 'unsupportedOperator' };

  const value = convertValue(column, legacyOperator, rawValue, masters);
  if ('reason' in value) return { reason: value.reason };

  return { converted: { index, legacyKey, legacyOperator, column, operator, value: value.value, rawValue } };
}

function toRows(groups: Converted[][]): FilterRow[] {
  return groups.flatMap((group, groupIndex) =>
    group.map((c, rowIndex) => ({
      // 展開で複製した and 行も別 id にする。id を共有すると行の編集・削除が
      // `rows.map(row => row.id !== id ...)` で複数行に同時に効いてしまう
      id: crypto.randomUUID(),
      combinator: rowIndex > 0 ? 'and' : groupIndex === 0 ? 'where' : 'or',
      column: c.column.key,
      operator: c.operator,
      value: c.value,
    })),
  );
}

/**
 * 取り込んだ結果が旧画面と変わる点を集める。判定は「変換できた条件」だけを見る
 * （落ちた条件は取り込まれないので、結果の差にはならない）。
 * 並びは LegacyImportNotice の定義順に固定し、表示側が順序を気にしなくて済むようにする。
 */
function collectNotices(converted: Converted[], rowCount: number): LegacyImportNotice[] {
  const notices: LegacyImportNotice[] = [];
  const some = (predicate: (c: Converted) => boolean) => converted.some(predicate);

  if (some((c) => c.legacyOperator === '含まない')) notices.push('notContains');
  if (some((c) => c.column.key === 'itemNames' && /\s/.test(c.rawValue))) notices.push('itemNameWithSpace');
  if (some((c) => c.column.key === 'itemNames' && c.legacyOperator === '完全一致')) notices.push('itemNameEquals');
  // 展開が起きたかは行数で判る。or 群と and 群が同時にあるときだけ行が増える
  if (rowCount > converted.length) notices.push('expanded');
  if (some((c) => REVIVED_COLUMN_KEYS.has(c.column.key))) notices.push('revivedColumn');
  // 大文字小文字を無視するのは text の比較（evaluate.ts の matchText が toLowerCase する）だけ。
  // enumSelect / idSelect は Set・ID の一致、number / time / boolean は文字列を比較しないので、
  // 英字が入っていても検索の挙動は変わらない（積み合わせの value "true" で誤報になる）
  if (some((c) => c.column.type === 'text' && /[A-Za-z]/.test(c.rawValue))) notices.push('caseInsensitive');
  if (some((c) => c.column.type === 'time')) notices.push('timeAcrossDays');
  if (some((c) => c.column.type === 'idSelect')) notices.push('idSelectByName');

  return notices;
}

/**
 * 旧画面の検索条件を新画面のフィルター行へ変換する（純関数）。
 *
 * 旧の意味は `(∨ or群) ∧ (∧ and群)` で行の順序を持たないが、新画面は
 * 「連続する and をグループにし、グループ同士を OR」で順序が意味を持つ。
 * 等価にするため選言標準形へ展開する。
 */
export function convertLegacyConditions(
  conditions: OrderSearchConditionEntity[],
  columns: FilterColumnDef[],
  masters: LegacyMastersState,
): LegacyImportResult {
  const columnsByKey = new Map(columns.map((c) => [c.key, c]));

  const issues: LegacyImportIssue[] = [];
  const convertedOr: Converted[] = [];
  const convertedAnd: Converted[] = [];
  let orTotal = 0;
  let andTotal = 0;

  conditions.forEach((condition, index) => {
    const isOr = condition.logicalOperator === 'or';
    if (isOr) orTotal += 1; else andTotal += 1;

    const outcome = convertCondition(condition, index, columnsByKey, masters);
    if ('reason' in outcome) {
      issues.push({ index, key: condition.key ?? '', operator: condition.operator ?? '', reason: outcome.reason });
      return;
    }
    (isOr ? convertedOr : convertedAnd).push(outcome.converted);
  });

  // or 群が空なら and 群だけの 1 グループ。旧画面は or 群が空だと常に 0 件になるが、
  // その癖は新画面の evaluator も踏襲していないので写さない
  const groups = convertedOr.length === 0
    ? [convertedAnd].filter((g) => g.length > 0)
    : convertedOr.map((or) => [or, ...convertedAnd]);

  const expandedRowCount = groups.reduce((sum, group) => sum + group.length, 0);
  // 行が爆発した条件式は新画面の UI で読めない。機械的に増やさず、プリセット全体を
  // 変換不能として利用者に判断させる。取り込まないので notices も出さない
  if (expandedRowCount > MAX_EXPANDED_ROWS) {
    return {
      rows: [],
      issues: [...issues, { index: -1, key: '', operator: '', reason: 'tooManyRows', rowCount: expandedRowCount }],
      notices: [],
      blocked: true,
      needsConfirmation: false,
    };
  }

  const rows = toRows(groups);

  // and 群が落ちると絞り込みが緩み、元の or 群が全滅すると `(O) ∧ A` が `A` だけになる。
  // どちらも対象が元より広がるので保存させない。判定は展開前の logicalOperator で行う
  // （展開後は and 行が各グループへ複製されて区別できない）
  const droppedAnd = andTotal - convertedAnd.length > 0;
  const orWipedOut = orTotal > 0 && convertedOr.length === 0;
  const mastersUnavailable = issues.some((i) => i.reason === 'mastersUnavailable');

  return {
    rows,
    issues,
    notices: collectNotices([...convertedOr, ...convertedAnd], rows.length),
    blocked: droppedAnd || orWipedOut || mastersUnavailable || rows.length === 0,
    needsConfirmation: orTotal - convertedOr.length > 0 && convertedOr.length > 0,
  };
}
